<script setup lang="ts">
import { ref } from 'vue';

import { PhxsgButton } from '../../Atoms/Button';
import { PhxsgHeading } from '../../Atoms/Heading';
import { PhxsgPicture } from '../../Atoms/Picture';
import { PhxsgText } from '../../Atoms/Text';

import { useLayoutMeasurement } from './use-layout-measurement';
import { useThumbnailSwap } from './use-thumbnail-swap';

import type { CollectionsBlockItem } from './phxsg-collections-block.vue';

const { items, alt = false } = defineProps<{
  items: CollectionsBlockItem[];
  alt?: boolean;
}>();

const rootRef = ref<HTMLElement | null>(null);
// Fixed content height to avoid jumps between items.
const contentHeight = ref(0);

const { currentIndex, currentItem, thumbnailIndexes, swapWithCurrent } = useThumbnailSwap(() => items);

useLayoutMeasurement(rootRef, updateContentHeight);

function updateContentHeight(): void {
  const root = rootRef.value;

  if (!alt || !root) {
    return;
  }

  const content = root.querySelector('.phxsg-collections-block-mobile__content');
  const measuredWidth = (content ?? root).getBoundingClientRect().width;
  const sizers = [...root.querySelectorAll<HTMLElement>('.phxsg-collections-block-mobile__content--sizer')];

  sizers.forEach((item) => {
    item.style.width = `${measuredWidth}px`;
  });

  contentHeight.value = Math.max(0, ...sizers.map((item) => item.getBoundingClientRect().height));
}
</script>

<template>
  <div ref="rootRef" :class="['phxsg-collections-block-mobile', { 'phxsg-collections-block-mobile--alt': alt }]">
    <Transition name="phxsg-collections-block-fade-content-mobile" mode="out-in">
      <div
        :key="`content-${currentIndex}`"
        class="phxsg-collections-block-mobile__content"
        :style="{ height: alt ? `${contentHeight}px` : undefined }"
      >
        <PhxsgHeading use="h2">{{ currentItem.title }}</PhxsgHeading>

        <PhxsgText v-if="currentItem.text" use="p" class="phxsg-collections-block-mobile__text">
          {{ currentItem.text }}
        </PhxsgText>

        <PhxsgButton
          v-if="!alt && currentItem.cta"
          use="a"
          :href="currentItem.cta.href"
          variant="fade"
          class="phxsg-collections-block-mobile__cta"
        >
          {{ currentItem.cta.label }}
        </PhxsgButton>
      </div>
    </Transition>

    <div class="phxsg-collections-block-mobile__media-wrapper">
      <div class="phxsg-collections-block-mobile__media">
        <Transition name="phxsg-collections-block-fade-image-mobile" mode="out-in">
          <PhxsgPicture
            :key="`image-${currentIndex}`"
            v-bind="currentItem.bigPicture"
            :type="alt ? 'full' : 'contain'"
            class="phxsg-collections-block-mobile__big-picture"
          />
        </Transition>
      </div>

      <PhxsgButton
        v-if="alt && currentItem.cta"
        use="a"
        :href="currentItem.cta.href"
        variant="fade"
        class="phxsg-collections-block-mobile__cta"
      >
        {{ currentItem.cta.label }}
      </PhxsgButton>

      <ul class="phxsg-collections-block__thumbnails">
        <li v-for="index in thumbnailIndexes" :key="`thumb-${index}`" class="phxsg-collections-block__thumbnail">
          <PhxsgPicture
            v-bind="items[index].thumbnail"
            type="contain"
            actionable
            :action-attrs="{ 'aria-label': items[index].title }"
            @click="swapWithCurrent(index)"
          />
        </li>
      </ul>
    </div>

    <div v-if="alt" class="phxsg-collections-block-mobile__content-sizers" aria-hidden="true">
      <div
        v-for="(item, index) in items"
        :key="`content-sizer-${item.title}-${index}`"
        class="phxsg-collections-block-mobile__content phxsg-collections-block-mobile__content--sizer"
      >
        <PhxsgHeading use="h2">{{ item.title }}</PhxsgHeading>

        <PhxsgText v-if="item.text" use="p" class="phxsg-collections-block-mobile__text">
          {{ item.text }}
        </PhxsgText>
      </div>
    </div>
  </div>
</template>
